import { useState } from 'react';
import { GitCompareArrows, X, Plus, Eye, EyeOff, Diff, Loader2 } from 'lucide-react';
import { Link, useLocation } from 'react-router';
import { useAuth } from '../hooks/useAuth';
import { useCompareActions } from '../hooks/useCompareActions';
import { useCompareProducts } from '../hooks/useCompareProducts';
import type { Product } from '../types/product';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';
import { useTranslation } from '../i18n';
import { formatDose } from '../utils/formatDose';
import HtmlContent from '../components/common/HtmlContent';

/**
 * Returns true when NOT all products share the same serialised value for a
 * given accessor.  Used to highlight rows where products differ.
 */
function valuesDiffer(
  items: Product[],
  accessor: (p: Product) => string,
): boolean {
  if (items.length <= 1) return false;
  const first = accessor(items[0]);
  return items.some((p) => accessor(p) !== first);
}

const formatPrice = (price: string | null): string => {
  const num = parseFloat(price ?? '0');
  return num.toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
  });
};

const highlightClass = (differ: boolean): string =>
  differ
    ? 'bg-amber-50 dark:bg-amber-900/20'
    : '';

/* ------------------------------------------------------------------ */
/* Diff helpers — compute +/- for array fields relative to the union   */
/* ------------------------------------------------------------------ */

/** For a given product, returns items it has (+) and items others have but it doesn't (-) */
function computeArrayDiff(
  product: Product,
  allProducts: Product[],
  accessor: (p: Product) => string[],
): { added: string[]; removed: string[] } {
  const mine = new Set(accessor(product));
  const union = new Set(allProducts.flatMap((p) => accessor(p)));
  const added = [...mine]; // items this product has
  const removed = [...union].filter((v) => !mine.has(v)); // items this product is missing
  return { added, removed };
}

const ComparisonPage = () => {
  const { isLoggedIn } = useAuth();
  const { compareList, removeFromCompare, clearCompare } = useCompareActions(isLoggedIn);
  const location = useLocation();
  const { t } = useTranslation();
  const [hidePrice, setHidePrice] = useState(true);
  const [diffMode, setDiffMode] = useState(false);

  const { products: comparedProducts, isLoading } = useCompareProducts(compareList);

  /* ------------------------------------------------------------------ */
  /* Loading state                                                      */
  /* ------------------------------------------------------------------ */
  if (isLoading && comparedProducts.length === 0) {
    return (
      <div className="space-y-8">
        <div className="flex items-center gap-3">
          <GitCompareArrows className="w-7 h-7 text-vivatis-600 dark:text-vivatis-400" />
          <h1 className="text-3xl font-bold text-anthracite-500 dark:text-gray-100">
            {t.comparison.title}
          </h1>
        </div>

        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Loader2 className="w-10 h-10 text-vivatis-600 dark:text-vivatis-400 animate-spin mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            {t.comparison.loading}
          </p>
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /* Empty state                                                        */
  /* ------------------------------------------------------------------ */
  if (comparedProducts.length === 0) {
    return (
      <div className="space-y-8">
        <div className="flex items-center gap-3">
          <GitCompareArrows className="w-7 h-7 text-vivatis-600 dark:text-vivatis-400" />
          <h1 className="text-3xl font-bold text-anthracite-500 dark:text-gray-100">
            {t.comparison.title}
          </h1>
        </div>

        <div className="flex flex-col items-center justify-center py-20 text-center">
          <GitCompareArrows className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {t.comparison.noProducts}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md">
            {t.comparison.noProductsDescription}
          </p>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 rounded-lg bg-vivatis-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-vivatis-700 transition-colors"
          >
            {t.comparison.browseProducts}
          </Link>
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /* Diff renderers for array fields                                    */
  /* ------------------------------------------------------------------ */
  const renderArrayDiff = (
    product: Product,
    accessor: (p: Product) => string[],
    renderItem: (value: string, type: 'added' | 'removed') => React.ReactNode,
  ) => {
    const { added, removed } = computeArrayDiff(product, comparedProducts, accessor);
    return (
      <div className="flex flex-col gap-1">
        {added.map((v) => renderItem(v, 'added'))}
        {removed.map((v) => renderItem(v, 'removed'))}
      </div>
    );
  };

  const diffBadge = (value: string, type: 'added' | 'removed') => (
    <div
      key={`${type}-${value}`}
      className={`inline-flex items-center gap-1 text-xs font-mono ${
        type === 'added'
          ? 'text-green-700 dark:text-green-400'
          : 'text-red-500 dark:text-red-400 line-through opacity-60'
      }`}
    >
      <span className="font-bold w-3">{type === 'added' ? '+' : '\u2212'}</span>
      {value}
    </div>
  );

  /* ------------------------------------------------------------------ */
  /* Row definitions                                                    */
  /* ------------------------------------------------------------------ */
  interface Row {
    key: string;
    label: string;
    render: (p: Product) => React.ReactNode;
    renderDiff?: (p: Product) => React.ReactNode;
    differ: boolean;
  }

  const rows: Row[] = [
    {
      key: 'name',
      label: t.comparison.rows.articleName,
      render: (p) => (
        <span className="font-medium text-gray-900 dark:text-gray-100">{p.name}</span>
      ),
      differ: valuesDiffer(comparedProducts, (p) => p.name),
    },
    {
      key: 'articleNumber',
      label: t.comparison.rows.articleNumber,
      render: (p) => (
        <span className="font-mono text-xs">{p.articleNumber}</span>
      ),
      differ: valuesDiffer(comparedProducts, (p) => p.articleNumber),
    },
    {
      key: 'partner',
      label: t.comparison.rows.manufacturer,
      render: (p) => p.partner?.name ?? '',
      differ: valuesDiffer(comparedProducts, (p) => p.partner?.name ?? ''),
    },
    {
      key: 'category',
      label: t.comparison.rows.category,
      render: (p) => p.category?.name ?? '',
      differ: valuesDiffer(comparedProducts, (p) => p.category?.name ?? ''),
    },
    {
      key: 'clinicalStudies',
      label: t.comparison.rows.clinicalStudies,
      render: (p) => (
        <span className="font-semibold">{p.clinicalStudies}</span>
      ),
      differ: valuesDiffer(comparedProducts, (p) => String(p.clinicalStudies)),
    },
    {
      key: 'novelFoodStatus',
      label: t.comparison.rows.novelFood,
      render: (p) => {
        const status = p.novelFoodStatus as keyof typeof t.comparison.novelFoodStatuses | null;
        const label = status ? (t.comparison.novelFoodStatuses[status] ?? p.novelFoodStatus) : '';
        return <span className="text-xs">{label}</span>;
      },
      differ: valuesDiffer(comparedProducts, (p) => p.novelFoodStatus ?? ''),
    },
    {
      key: 'bioactiveIngredients',
      label: t.comparison.rows.bioactiveIngredients,
      render: (p) => (
        <HtmlContent html={p.bioactiveIngredients} className="text-xs leading-relaxed" />
      ),
      differ: valuesDiffer(comparedProducts, (p) => p.bioactiveIngredients ?? ''),
    },
    {
      key: 'dosage',
      label: t.comparison.rows.dailyDosage,
      render: (p) => formatDose(p),
      differ: valuesDiffer(comparedProducts, (p) => formatDose(p)),
    },
    {
      key: 'origin',
      label: t.comparison.rows.sourceOrigin,
      render: (p) => p.origin?.name ?? '',
      differ: valuesDiffer(comparedProducts, (p) => p.origin?.name ?? ''),
    },
    {
      key: 'dailyPrice',
      label: t.comparison.rows.price,
      render: (p) => (
        <span className="font-semibold">
          {formatPrice(p.dailyDosePrice)}
        </span>
      ),
      differ: valuesDiffer(
        comparedProducts,
        (p) => String(p.dailyDosePrice),
      ),
    },
    {
      key: 'applications',
      label: t.comparison.rows.applications,
      render: (p) => (
        <div className="flex flex-wrap gap-1">
          {p.applications.map((app) => (
            <Badge key={app.id} variant="success">
              {app.name}
            </Badge>
          ))}
        </div>
      ),
      renderDiff: (p) =>
        renderArrayDiff(p, (pr) => pr.applications.map((a) => a.name), diffBadge),
      differ: valuesDiffer(comparedProducts, (p) =>
        [...p.applications.map((a) => a.name)].sort().join(','),
      ),
    },
  ];

  /* Apply filters */
  let visibleRows = rows;
  if (hidePrice) {
    visibleRows = visibleRows.filter((r) => r.key !== 'dailyPrice');
  }
  if (diffMode) {
    visibleRows = visibleRows.filter((r) => r.differ);
  }

  /* ------------------------------------------------------------------ */
  /* Render                                                             */
  /* ------------------------------------------------------------------ */
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <GitCompareArrows className="w-7 h-7 text-vivatis-600 dark:text-vivatis-400" />
          <h1 className="text-3xl font-bold text-anthracite-500 dark:text-gray-100">
            {t.comparison.title}
          </h1>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {t.comparison.productCount(comparedProducts.length, 4)}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {comparedProducts.length < 4 && (
            <Link to="/products">
              <Button
                variant="secondary"
                size="sm"
                icon={<Plus className="w-4 h-4" />}
              >
                {t.comparison.addProduct}
              </Button>
            </Link>
          )}
          <Button
            variant="ghost"
            size="sm"
            icon={<X className="w-4 h-4" />}
            onClick={clearCompare}
          >
            {t.comparison.removeAll}
          </Button>
        </div>
      </div>

      {/* Toggle switches */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Diff mode toggle */}
        <button
          type="button"
          onClick={() => setDiffMode(!diffMode)}
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
            diffMode
              ? 'bg-vivatis-600 text-white'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <Diff className="w-4 h-4" />
          {diffMode ? t.comparison.showingDifferencesOnly : t.comparison.showDifferencesOnly}
        </button>

        {/* Hide price toggle */}
        <button
          type="button"
          onClick={() => setHidePrice(!hidePrice)}
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
            hidePrice
              ? 'bg-gray-700 dark:bg-gray-600 text-white'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          {hidePrice ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          {hidePrice ? t.comparison.priceHidden : t.comparison.hidePrice}
        </button>
      </div>

      {/* Comparison table */}
      <div className="overflow-x-auto -mx-4 px-4 pb-2">
        <table className="w-full min-w-[640px] border-collapse text-sm">
          {/* Product header columns */}
          <thead>
            <tr>
              {/* Label column */}
              <th className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-900 min-w-[120px] max-w-[160px] p-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700" />

              {comparedProducts.map((product) => (
                <th
                  key={product.id}
                  className="min-w-[200px] p-3 text-left border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <Link
                        to={`/products/${product.id}`}
                        state={{ backgroundLocation: location }}
                        className="font-semibold text-gray-900 dark:text-gray-100 hover:text-vivatis-600 dark:hover:text-vivatis-400 transition-colors"
                      >
                        {product.name}
                      </Link>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {product.partner?.name ?? ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      title={t.comparison.removeFromComparison}
                      className="shrink-0 p-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      onClick={() => removeFromCompare(String(product.id))}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          {/* Data rows */}
          <tbody>
            {visibleRows.length === 0 ? (
              <tr>
                <td
                  colSpan={comparedProducts.length + 1}
                  className="p-8 text-center text-gray-500 dark:text-gray-400"
                >
                  {t.comparison.allIdentical}
                </td>
              </tr>
            ) : (
              visibleRows.map((row) => (
                <tr key={row.key}>
                  {/* Label cell */}
                  <td className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-900 p-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700 min-w-[120px] max-w-[160px]">
                    {row.label}
                    {diffMode && row.differ && (
                      <span className="ml-1.5 text-amber-500">*</span>
                    )}
                  </td>

                  {/* Value cells */}
                  {comparedProducts.map((product) => (
                    <td
                      key={product.id}
                      className={`p-3 text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 align-top ${highlightClass(row.differ)}`}
                    >
                      {diffMode && row.renderDiff
                        ? row.renderDiff(product)
                        : row.render(product)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ComparisonPage;
